import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Folder, ArrowRight, Loader2, Bookmark, BookOpen } from 'lucide-react';
import AppShell from '@/components/AppShell';
import { gradeAccent } from '@/lib/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Subject filter options — must match `subject` returned by gradeAccent()
const SUBJECTS = [
  { key: 'all', label: 'All Subjects' },
  { key: 'Civics', label: 'Civics' },
  { key: 'US History', label: 'US History' },
  { key: 'World History', label: 'World History' },
  { key: 'Government', label: 'Government' },
];

/**
 * Collections workspace — the user's personal "pinned" units.
 * Backed by /api/collections/pinned and /api/collections/toggle/:unitId.
 *
 * Phase 4 enhancement: filter pinned lessons by subject (Civics / US History /
 * World History / Government). Subject is derived from each unit's
 * grade_level via gradeAccent().
 */
export default function CollectionsPage() {
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubject, setActiveSubject] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/collections/pinned`);
      setUnits(res.data?.units || []);
    } catch (err) {
      setUnits([]);
      if (err?.response?.status === 401) {
        toast.error('Please sign in to view your collection');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const unpin = async (unitId) => {
    try {
      await axios.post(`${API}/collections/toggle/${unitId}`);
      setUnits(prev => prev.filter(u => u.id !== unitId));
      toast.success('Removed from collection');
    } catch (_e) {
      toast.error('Could not update collection');
    }
  };

  // Count pinned units per subject for filter chip badges
  const subjectCounts = useMemo(() => {
    const counts = { all: units.length };
    units.forEach(u => {
      const subj = gradeAccent(u.grade_level).subject;
      counts[subj] = (counts[subj] || 0) + 1;
    });
    return counts;
  }, [units]);

  // Filter visible units by active subject
  const visible = useMemo(() => {
    if (activeSubject === 'all') return units;
    return units.filter(u => gradeAccent(u.grade_level).subject === activeSubject);
  }, [units, activeSubject]);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-10" data-testid="collections-page">
        <div className="mb-8 flex items-start gap-3">
          <Folder className="h-6 w-6 text-[#D4AF37] mt-1" />
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">Workspace</p>
            <h1 className="font-serif-display text-3xl text-[#14213D] tracking-tight">My Collection</h1>
            <p className="text-sm text-[#5A6577] mt-1 max-w-2xl">Lessons you&rsquo;ve pinned for quick access &mdash; your portable curriculum file. Pin or unpin any lesson from a unit&rsquo;s detail page.</p>
          </div>
        </div>

        {/* Subject filter — only shown when there are pins */}
        {!loading && units.length > 0 && (
          <div className="bg-[#F9F8F4] border border-[#EBE7E0] rounded-lg p-3 mb-6" data-testid="subject-filter">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] uppercase tracking-[0.18em] text-[#5A6577] font-semibold mr-2 hidden sm:inline">Filter by subject</span>
              {SUBJECTS.map(s => {
                const count = subjectCounts[s.key] || 0;
                const isActive = activeSubject === s.key;
                const isAll = s.key === 'all';
                // Disable subjects with 0 pinned units (except 'all')
                const disabled = !isAll && count === 0;
                return (
                  <button
                    key={s.key}
                    onClick={() => !disabled && setActiveSubject(s.key)}
                    disabled={disabled}
                    className={`text-xs px-3 py-1.5 rounded-full transition-colors inline-flex items-center gap-1.5 border ${
                      isActive
                        ? 'bg-[#14213D] text-[#FDFBF7] border-[#14213D]'
                        : disabled
                        ? 'bg-white text-[#CBD5E0] border-[#EBE7E0] cursor-not-allowed'
                        : 'bg-white text-[#2D3748] border-[#EBE7E0] hover:border-[#14213D]/30'
                    }`}
                    data-testid={`subject-filter-${s.key.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {s.label}
                    <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium ${
                      isActive ? 'bg-[#FDFBF7]/20 text-[#FDFBF7]' : 'bg-[#F9F8F4] text-[#5A6577]'
                    }`}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-[#5A6577]">
            <Loader2 className="h-6 w-6 animate-spin mb-3" />
            <p className="text-sm">Loading your collection&hellip;</p>
          </div>
        ) : units.length === 0 ? (
          <div className="text-center py-16 bg-[#F9F8F4] border border-[#EBE7E0] rounded-lg" data-testid="empty-collection">
            <Bookmark className="h-10 w-10 text-[#E2E8F0] mx-auto mb-3" />
            <h3 className="font-serif-display text-lg text-[#14213D] mb-2">No pins yet</h3>
            <p className="text-sm text-[#5A6577] max-w-md mx-auto mb-5">
              When you find a lesson you want to come back to, click the <Bookmark className="inline h-3.5 w-3.5 align-text-bottom" /> Pin button on its detail page &mdash; it&rsquo;ll show up here.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center gap-2 bg-[#14213D] hover:bg-[#1D2D50] text-[#FDFBF7] text-sm font-medium px-5 py-2.5 rounded-md transition-colors"
              data-testid="empty-collection-cta"
            >
              <BookOpen className="h-4 w-4" /> Browse lessons
            </button>
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-12 bg-[#F9F8F4] border border-[#EBE7E0] rounded-lg" data-testid="no-subject-results">
            <p className="text-sm text-[#5A6577]">No pinned lessons in <span className="font-medium text-[#14213D]">{activeSubject}</span>. Try another subject.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#5A6577] mb-4">
              {visible.length} pinned lesson{visible.length === 1 ? '' : 's'}
              {activeSubject !== 'all' && <> in <span className="font-medium text-[#14213D]">{activeSubject}</span></>}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {visible.map((unit) => {
                const accent = gradeAccent(unit.grade_level);
                return (
                  <article
                    key={unit.id}
                    className="group bg-white border border-[#EBE7E0] rounded-md p-5 hover:shadow-md hover:border-[#14213D]/20 transition-all relative overflow-hidden"
                    data-testid={`pinned-card-${unit.id}`}
                  >
                    <div className="absolute top-0 left-0 w-1 h-full" style={{ background: accent.hex, opacity: 0.7 }} />
                    <div className="flex items-start justify-between mb-2 pl-1.5">
                      <span className="text-[11px] uppercase tracking-[0.15em] font-semibold" style={{ color: accent.hex }}>
                        Grade {unit.grade_level} &middot; {accent.subject}
                      </span>
                      <button
                        onClick={() => unpin(unit.id)}
                        className="text-[#D4AF37] hover:text-[#14213D] transition-colors"
                        aria-label="Unpin lesson"
                        data-testid={`unpin-${unit.id}`}
                      >
                        <Bookmark className="h-4 w-4 fill-current" />
                      </button>
                    </div>
                    <h3 className="font-serif-display text-base text-[#14213D] leading-tight mb-2 line-clamp-2 pl-1.5">{unit.title}</h3>
                    <p className="text-xs text-[#5A6577] line-clamp-3 leading-relaxed mb-3 pl-1.5">{unit.description}</p>
                    <button
                      onClick={() => navigate(`/unit/${unit.id}`)}
                      className="text-xs font-medium text-[#14213D] inline-flex items-center gap-1 hover:text-[#D4AF37] transition-colors pl-1.5"
                      data-testid={`open-pinned-${unit.id}`}
                    >
                      Open lesson <ArrowRight className="h-3 w-3" />
                    </button>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
